import React, { useContext, useMemo, useState } from 'react';
import { CompanyContext } from '../../../../context/CompanyContext';
import { CustomFieldContext } from '../../../../context/CustomFieldContext';
import { SuperUserContext } from '../../../../context/SuperUserContext';
import {
  CreateCustomFieldResponse,
  CreateCustomFieldVariables,
  CustomFieldMutations,
  UpdateCustomFieldResponse,
  UpdateCustomFieldVariables,
} from '../../../../graphql/customField.graphql';
import useMutation from '../../../../hooks/useMutation';
import { useToast } from '../../../../providers/ToastProvider';
import { CustomField, CustomFieldEntityType, CustomFieldType, CustomFieldUtil } from '../../../../types/custom_field';
import { CustomFieldInput } from '../../../../types/custom_field/customField.input';
import { SuperUserRole } from '../../../../types/superUser';
import { TranslationKey } from '../../../../types/translation/TranslationKey';
import { t, translate } from '../../../../types/translation/Translator';
import { AddButton, Button, CancelButton } from '../../../components/Button';
import { Checkbox } from '../../../components/Checkbox';
import { Divider } from '../../../components/Divider';
import { DropdownSelect, DropdownSearchSelect } from '../../../components/Dropdown';
import { ActionModal } from '../../../components/Modal';
import Table from '../../../components/Table';
import { TextInput } from '../../../components/TextInput';
import { ToastType } from '../../../components/Toast';
import Icon, { IconType } from '../../../icons/Icon';
import { CustomFieldDateDefaultValue } from './CustomFieldDateDefaultValue';

interface CustomFieldModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  customField: CustomField | undefined;
}

export default function CustomFieldModal({ open, setOpen, customField }: CustomFieldModalProps) {
  const { showToast } = useToast();

  const { currentCompany } = useContext(CompanyContext);
  const { customFields, setCustomFields } = useContext(CustomFieldContext);
  const { superUser } = useContext(SuperUserContext);

  const [input, setInput] = useState(
    new CustomFieldInput(
      customField || { companyId: currentCompany.id, entityType: CustomFieldEntityType.productMasterData },
    ),
  );
  const [newValue, setNewValue] = useState<string>('');

  const handleAddValue = () => {
    const newInput = input.addValue(newValue);
    if (!newInput)
      return showToast({
        type: ToastType.error,
        title: translate({ key: TranslationKey.customFieldAddedValueAlreadyExistsToastTitle }),
      });
    setInput(newInput);
  };

  const handleActionComplete = (customField: CustomField) => {
    setCustomFields(new Map(customFields.set(customField.id, customField)));
    setOpen(false);
  };

  const [create, { loading: createLoading }] = useMutation<CreateCustomFieldResponse, CreateCustomFieldVariables>(
    CustomFieldMutations.create,
    {
      variables: { customFields: [input.forCreate()] },
      onCompleted: response => {
        handleActionComplete(response.createCustomField[0]);
        showToast({
          type: ToastType.success,
          title: translate({ key: TranslationKey.customFieldCreatedSuccessfullyToastTitle }),
        });
      },
    },
  );

  const [update, { loading: updateLoading }] = useMutation<UpdateCustomFieldResponse, UpdateCustomFieldVariables>(
    CustomFieldMutations.update,
    {
      variables: { customFields: [input.forUpdate()] },
      onCompleted: response => {
        handleActionComplete(response.updateCustomField[0]);
        showToast({
          type: ToastType.success,
          title: translate({ key: TranslationKey.customFieldUpdatedSuccessfullyToastTitle }),
        });
      },
    },
  );

  const handleSave = () => {
    if (customField) {
      update();
    } else {
      create();
    }
  };

  const potentialParents = useMemo(() => {
    return [...customFields.values()].filter(
      cf => cf.entityType === input.entityType && cf.type === CustomFieldType.listOfTuples && cf.id !== input.id,
    );
  }, [customFields, input]);

  const loading = createLoading || updateLoading;

  const footer = (
    <div className='flex justify-end gap-2'>
      <CancelButton onClick={() => setOpen(false)} disabled={loading} />
      <Button
        text={t(customField ? TranslationKey.update : TranslationKey.create).singular.upper}
        onClick={() => handleSave()}
        loading={loading}
        disabled={!input.validate() || !input.hasChanges(customField)}
      />
    </div>
  );

  const defaultValueInput = () => {
    switch (input.type) {
      case CustomFieldType.text:
        return (
          <TextInput
            value={input.defaultValue}
            label={translate({ key: TranslationKey.defaultValue })}
            placeholder={translate({ key: TranslationKey.defaultValue })}
            onChange={value => setInput(input.withDefaultValue(value))}
            disabled={!input.entityType || !input.type}
          />
        );
      case CustomFieldType.bool:
        return (
          <div className='flex items-end pb-2.5'>
            <Checkbox
              label={translate({ key: TranslationKey.defaultValue })}
              value={input.defaultValue === 'true' ? true : false}
              onChange={value => setInput(input.withDefaultValue(value ? 'true' : 'false'))}
              disabled={!input.entityType || !input.type}
            />
          </div>
        );
      case CustomFieldType.date:
        return <CustomFieldDateDefaultValue input={input} setInput={setInput} />;
      case CustomFieldType.listOfValues:
        return (
          <DropdownSearchSelect
            disabled={!input.values.length || !input.entityType || !input.type}
            items={input.values}
            label={translate({ key: TranslationKey.defaultValue })}
            placeholder={translate({ key: TranslationKey.defaultValue })}
            value={input.defaultValue}
            textBuilder={(item?: string) => item || ''}
            onSelect={item => setInput(input.withDefaultValue(item || undefined))}
          />
        );
    }
  };

  return (
    <ActionModal footer={footer} title={'Custom field'} open={open} setOpen={setOpen} className='max-w-[720px]'>
      <div className='grid w-full gap-4'>
        <div className='grid grid-cols-2 gap-4'>
          <DropdownSelect
            disabled={!!customField}
            items={
              [...Object.keys(CustomFieldEntityType)].sort((a, b) => a.localeCompare(b)) as CustomFieldEntityType[]
            }
            label={translate({ key: TranslationKey.entityType })}
            value={input.entityType}
            textBuilder={cf => CustomFieldUtil.customFieldEntityTypeToString(cf)}
            onSelect={cf => setInput(input.withEntityType(cf))}
            placeholder={translate({ key: TranslationKey.entityType })}
          />
          {input.validEntitySubtypes().length ? (
            <DropdownSearchSelect
              disabled={!!customField}
              items={input.validEntitySubtypes()}
              label={translate({ key: TranslationKey.entitySubtype })}
              value={input.entitySubtype}
              textBuilder={cf => CustomFieldUtil.customFieldEntitySubtypeToString(cf)}
              onSelect={cf => setInput(input.withEntitySubtype(cf))}
              placeholder={translate({ key: TranslationKey.entitySubtype })}
            />
          ) : null}
        </div>

        {input.validEntityParentTypes().length || input.validEntityChildTypes().length ? (
          <div className='grid grid-cols-2 gap-4'>
            {input.validEntityParentTypes().length ? (
              <DropdownSearchSelect
                disabled={!!customField}
                placeholder={translate({ key: TranslationKey.entityParentType })}
                label={translate({ key: TranslationKey.entityParentType })}
                textBuilder={value => CustomFieldUtil.customFieldEntityParentTypeToLocalizedString(value)}
                items={input.validEntityParentTypes()}
                value={input.entityParentType}
                onSelect={value => setInput(input.withEntityParentType(value))}
              />
            ) : null}
            {input.validEntityChildTypes().length ? (
              <DropdownSearchSelect
                disabled={!!customField}
                label={translate({ key: TranslationKey.entityChildType })}
                placeholder={translate({ key: TranslationKey.entityChildType })}
                textBuilder={value => CustomFieldUtil.customFieldEntityChildTypeToString(value)}
                items={input.validEntityChildTypes()}
                value={input.entityChildType}
                onSelect={value => setInput(input.withEntityChildType(value || undefined))}
              />
            ) : null}
          </div>
        ) : null}

        <Divider />

        <div className='grid grid-cols-2 gap-4'>
          <TextInput
            mandatory
            label={translate({ key: TranslationKey.name })}
            placeholder={translate({ key: TranslationKey.name })}
            onChange={name => setInput(input.withName(name))}
            value={input.name}
          />
          <TextInput
            value={input.index.toString()}
            type={'number'}
            label={translate({ key: TranslationKey.priority })}
            placeholder={translate({ key: TranslationKey.priority })}
            onChange={name => setInput(input.withIndex(parseFloat(name)))}
          />
        </div>

        <div className='grid grid-cols-2 gap-4'>
          <DropdownSelect
            items={input.validTypes()}
            label={translate({ key: TranslationKey.type })}
            value={input.type}
            textBuilder={cf => CustomFieldUtil.customFieldTypeToString(cf)}
            onSelect={cf => setInput(input.withType(cf))}
            placeholder={translate({ key: TranslationKey.type })}
            disabled={!input.entityType || !!customField}
          />

          {input.validReference() ? (
            <DropdownSearchSelect
              tooltip={translate({ key: TranslationKey.customFieldReferenceTooltip })}
              value={input.reference}
              onSelect={item => setInput(input.withReference(item))}
              label={translate({ key: TranslationKey.reference })}
              placeholder={translate({ key: TranslationKey.reference })}
              items={[
                CustomFieldEntityType.companyRole,
                CustomFieldEntityType.contact,
                CustomFieldEntityType.productMasterData,
              ]}
              textBuilder={item => {
                if (item === CustomFieldEntityType.companyRole) {
                  return translate({ key: TranslationKey.user });
                } else if (item === CustomFieldEntityType.contact) {
                  return translate({ key: TranslationKey.contact });
                } else if (item === CustomFieldEntityType.productMasterData) {
                  return translate({ key: TranslationKey.productMasterData });
                } else {
                  return translate({ key: TranslationKey.unknown });
                }
              }}
            />
          ) : null}
        </div>

        <div className='grid grid-cols-2'>{defaultValueInput()}</div>

        <div className='grid gap-2'>
          {input.type !== CustomFieldType.bool ? (
            <Checkbox
              label={translate({ key: TranslationKey.mandatory })}
              value={input.mandatory}
              onChange={mandatory => setInput(input.withMandatory(mandatory))}
            />
          ) : null}

          {superUser?.role === SuperUserRole.administrator && (
            <>
              <Checkbox
                tooltip={translate({ key: TranslationKey.customFieldGeneratedTooltip })}
                label={translate({ key: TranslationKey.generated })}
                value={input.generated}
                onChange={generated => setInput(input.withGenerated(generated))}
              />

              <Checkbox
                tooltip={translate({ key: TranslationKey.customFieldHideTooltip })}
                label={translate({ key: TranslationKey.hide })}
                value={input.hidden}
                onChange={hidden => setInput(input.withHidden(hidden))}
              />
            </>
          )}

          <Checkbox
            label={translate({ key: TranslationKey.displayMobile })}
            value={input.displayMobile}
            tooltip={
              <div className='grid gap-2'>
                <p>{translate({ key: TranslationKey.displayMobileTooltip })}</p>
                <img
                  src={`${process.env.REACT_APP_STORAGE_URL}/ventory/custom-field-display-mobile-image.webp`}
                  alt='Display mobile example'
                  className='h-auto max-h-[320px] w-full rounded-2 object-contain'
                />
              </div>
            }
            onChange={displayMobile => setInput(input.withDisplayMobile(displayMobile))}
          />

          {customField?.entityType === CustomFieldEntityType.order ? (
            <>
              <Checkbox
                label={translate({ key: TranslationKey.onCreate })}
                value={input.onCreate}
                onChange={onCreate => setInput(input.withOnCreate(onCreate))}
              />
              <Checkbox
                label={translate({ key: TranslationKey.manual })}
                value={input.manual || false}
                onChange={onCreate => setInput(input.withManual(onCreate))}
              />
            </>
          ) : null}

          {potentialParents.length ? (
            <DropdownSearchSelect
              placeholder={translate({ key: TranslationKey.parent })}
              items={potentialParents.map(cf => cf.id)}
              textBuilder={id => customFields.get(id)?.name || ''}
              value={input.parentId}
              onSelect={id => setInput(input.withParentId(id))}
            />
          ) : null}
        </div>

        {(input.type === CustomFieldType.listOfValues && !input.reference) ||
        input.type === CustomFieldType.listOfTuples ? (
          <div className='grid gap-2'>
            <div className='flex gap-2'>
              <TextInput
                placeholder={translate({ key: TranslationKey.valueName })}
                onChange={setNewValue}
                value={newValue}
              />

              <AddButton onClick={handleAddValue} disabled={input.hasValue(newValue)} />
            </div>

            <Table
              modal
              customPagination={5}
              items={input.values}
              headers={[
                {
                  key: 'name',
                  name: translate({ key: TranslationKey.name }),
                  text: (item: string) => (item.includes(':::') ? item.split(':::')[0] : item),
                },
                {
                  key: 'value',
                  name: translate({ key: TranslationKey.value }),
                  text: (value: string) => (
                    <TextInput
                      value={value.includes(':::') ? value.split(':::')[1] : ''}
                      onChange={item => {
                        const idx = input.values.findIndex(i => i === value);
                        if (idx < 0) return;

                        if (value.includes(':::')) {
                          const v = value.split(':::');
                          if (!item) {
                            input.values[idx] = v[0];
                          } else {
                            input.values[idx] = `${v[0]}:::${item}`;
                          }
                        } else {
                          input.values[idx] = `${value}:::${item}`;
                        }
                        setInput(new CustomFieldInput(input));
                      }}
                    />
                  ),
                },
                {
                  key: 'remove',
                  name: '',
                  text: (item: string) => (
                    <Icon
                      stroke={'stroke-body'}
                      size={'h-4 w-4'}
                      type={IconType.trash1}
                      onClick={() => setInput(input.removeValue(item))}
                    />
                  ),
                },
              ].filter(item => {
                if (item.key === 'value' && input.type !== CustomFieldType.listOfTuples) return false;
                return true;
              })}
            />
          </div>
        ) : null}
      </div>
    </ActionModal>
  );
}
